import { ChartKey, ChartTimerName } from '@frontend/chart/types';
import { Application, Feature } from '@frontend/functionality/types';

/** Backend list endpoints: which column `after` / `before` apply to (separately). */
export const INSIGHT_HEATMAP_TIME_FIELD_AFTER = 'time_field_after' as const;
export const INSIGHT_HEATMAP_TIME_FIELD_BEFORE = 'time_field_before' as const;

export type InsightHeatmapCellDrilldownKind = 'timers' | 'transactions' | 'packages';

export type InsightHeatmapCellTimeField = 'update_timestamp' | 'creation_timestamp';

export interface InsightHeatmapCellDrilldown {
    kind: InsightHeatmapCellDrilldownKind;
    application: Application;
    feature?: Feature;
    /** Completed timer names for timer-activity heatmaps. */
    timerNames?: ChartTimerName[];
    /** Transaction state filter (e.g. replenishment completed). */
    transactionState?: string;
    /**
     * Timestamp column for both after and before bounds.
     * Timer activity uses update_timestamp (completion); volume charts use creation_timestamp.
     */
    timeField: InsightHeatmapCellTimeField;
}

const dropOffAndPickUpTimers = [ChartTimerName.TRANSACTION_DROP_OFF_PENDING, ChartTimerName.TRANSACTION_PICK_UP_PENDING];

const timerActivity = (application: Application, feature?: Feature): InsightHeatmapCellDrilldown => ({
    kind: 'timers',
    application,
    feature,
    timerNames: dropOffAndPickUpTimers,
    timeField: 'update_timestamp'
});

const packageVolume = (application: Application, feature: Feature): InsightHeatmapCellDrilldown => ({
    kind: 'packages',
    application,
    feature,
    timeField: 'creation_timestamp'
});

const INSIGHT_HEATMAP_CELL_DRILLDOWNS: Partial<Record<ChartKey, InsightHeatmapCellDrilldown>> = {
    [ChartKey.PUDO_TRANSACTION_TIMER_ACTIVITY_WEEKDAY_HEATMAP]: timerActivity(Application.PUDO),
    [ChartKey.PUDO_TRANSACTION_TIMER_ACTIVITY_HOUR_DAY_HEATMAP]: timerActivity(Application.PUDO),
    [ChartKey.NIVALIZ_OUTBOUND_TRANSACTION_TIMER_ACTIVITY_WEEKDAY_HEATMAP]: timerActivity(Application.NIVALIZ, Feature.OUTBOUND_TRANSACTION),
    [ChartKey.NIVALIZ_INBOUND_TRANSACTION_TIMER_ACTIVITY_WEEKDAY_HEATMAP]: timerActivity(Application.NIVALIZ, Feature.INBOUND_TRANSACTION),
    [ChartKey.NIVALIZ_OUTBOUND_TRANSACTION_TIMER_ACTIVITY_HOUR_DAY_HEATMAP]: timerActivity(Application.NIVALIZ, Feature.OUTBOUND_TRANSACTION),
    [ChartKey.NIVALIZ_INBOUND_TRANSACTION_TIMER_ACTIVITY_HOUR_DAY_HEATMAP]: timerActivity(Application.NIVALIZ, Feature.INBOUND_TRANSACTION),
    [ChartKey.VENDING_PACKAGE_VOLUME_HOUR_DAY_HEATMAP]: packageVolume(Application.VENDING, Feature.OUTBOUND_TRANSACTION),
    [ChartKey.VENDING_REPLENISHMENT_TRANSACTION_VOLUME_HOUR_DAY_HEATMAP]: {
        kind: 'transactions',
        application: Application.VENDING,
        feature: Feature.INBOUND_TRANSACTION,
        transactionState: 'transaction_completed',
        timeField: 'creation_timestamp'
    },
    [ChartKey.LENDING_OUTBOUND_PACKAGE_VOLUME_WEEKDAY_HEATMAP]: packageVolume(Application.LENDING, Feature.OUTBOUND_TRANSACTION),
    [ChartKey.LENDING_RETURN_PACKAGE_VOLUME_WEEKDAY_HEATMAP]: packageVolume(Application.LENDING, Feature.RETURN_TRANSACTION),
    [ChartKey.LENDING_OUTBOUND_PACKAGE_VOLUME_HOUR_DAY_HEATMAP]: packageVolume(Application.LENDING, Feature.OUTBOUND_TRANSACTION),
    [ChartKey.LENDING_RETURN_PACKAGE_VOLUME_HOUR_DAY_HEATMAP]: packageVolume(Application.LENDING, Feature.RETURN_TRANSACTION)
};

export const getInsightHeatmapCellDrilldown = (chartKey: ChartKey | string): InsightHeatmapCellDrilldown | null =>
    INSIGHT_HEATMAP_CELL_DRILLDOWNS[chartKey as ChartKey] ?? null;
